import { bindDialog } from '@theme/dialog';

const configElement = document.getElementById('search-config');
const config = configElement ? JSON.parse(configElement.textContent || '{}') : {};
const dialogElement = document.getElementById('search-dialog');
const dialog = dialogElement instanceof HTMLDialogElement ? dialogElement : null;
const input = dialog?.querySelector('[data-dialog-focus]');
const results = dialog?.querySelector('#search-results');
const status = dialog?.querySelector('[data-search-status]');

bindDialog(dialog);

let controller = null;
let timer = 0;
let activeIndex = -1;

const announce = (message) => {
  if (!status) return;
  status.textContent = '';
  window.setTimeout(() => {
    status.textContent = message;
  }, 30);
};

const options = () => (results ? [...results.querySelectorAll('[role="option"]')] : []);

const setActive = (index) => {
  const items = options();
  activeIndex = items.length ? Math.max(0, Math.min(index, items.length - 1)) : -1;
  items.forEach((item, itemIndex) => {
    item.setAttribute('aria-selected', itemIndex === activeIndex ? 'true' : 'false');
  });
  if (input instanceof HTMLInputElement) {
    const selected = items[activeIndex];
    input.setAttribute('aria-activedescendant', selected?.id || '');
  }
  items[activeIndex]?.scrollIntoView({ block: 'nearest' });
};

const group = (title, nodes) => {
  if (!nodes.length) return null;
  const section = document.createElement('section');
  section.className = 'search-dialog__group';
  const heading = document.createElement('h3');
  heading.textContent = title;
  const list = document.createElement('ul');
  list.className = 'search-dialog__list';
  for (const node of nodes) {
    const item = document.createElement('li');
    item.append(node);
    list.append(item);
  }
  section.append(heading, list);
  return section;
};

const optionLink = (href, text, imageSrc) => {
  const link = document.createElement('a');
  link.className = 'search-dialog__option';
  link.href = href;
  link.role = 'option';
  link.id = `search-option-${Math.random().toString(36).slice(2, 8)}`;
  link.setAttribute('aria-selected', 'false');
  if (imageSrc) {
    const image = document.createElement('img');
    image.src = imageSrc;
    image.alt = '';
    link.append(image);
  }
  const label = document.createElement('span');
  label.textContent = text;
  link.append(label);
  return link;
};

const imageSource = (resource) => {
  if (typeof resource.image === 'string') return resource.image;
  if (typeof resource.featured_image?.url === 'string') return resource.featured_image.url;
  if (typeof resource.image?.url === 'string') return resource.image.url;
  return '';
};

const renderResults = (payload, query) => {
  if (!results || !(input instanceof HTMLInputElement)) return;
  results.replaceChildren();
  activeIndex = -1;
  input.setAttribute('aria-activedescendant', '');

  const resources = payload?.resources?.results || {};
  const queries = (resources.queries || []).map((item) => optionLink(item.url, item.text, ''));
  const products = (resources.products || []).map((item) =>
    optionLink(item.url, item.price ? `${item.title} ${item.price}` : item.title, imageSource(item))
  );
  const collections = (resources.collections || []).map((item) => optionLink(item.url, item.title, imageSource(item)));
  const pages = (resources.pages || []).map((item) => optionLink(item.url, item.title, ''));
  const articles = (resources.articles || []).map((item) => optionLink(item.url, item.title, imageSource(item)));

  const sections = [
    group(config.suggestions, queries),
    group(config.products, products),
    group(config.collections, collections),
    group(config.pages, pages),
    group(config.articles, articles),
  ].filter(Boolean);

  if (!sections.length) {
    const empty = document.createElement('p');
    empty.textContent = config.noResults || '';
    results.append(empty);
    announce(`${config.noResults || ''} ${query}`.trim());
    return;
  }

  results.append(...sections);
  announce(`${options().length}`);
};

const search = async (query) => {
  controller?.abort();
  controller = new AbortController();
  if (!results || !dialog) return;
  dialog.setAttribute('aria-busy', 'true');
  announce(config.loading || '');

  const url = new URL(`${config.url}.json`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product,query,collection,page,article');
  url.searchParams.set('resources[limit]', '6');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[options][unavailable_products]', 'last');

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Search failed');
    const payload = await response.json();
    renderResults(payload, query);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    if (results) {
      results.replaceChildren();
      const message = document.createElement('p');
      message.textContent = config.noResults || '';
      results.append(message);
    }
  } finally {
    dialog.removeAttribute('aria-busy');
  }
};

if (input instanceof HTMLInputElement) {
  input.addEventListener('input', () => {
    const query = input.value.trim();
    input.setAttribute('aria-expanded', query ? 'true' : 'false');
    window.clearTimeout(timer);
    if (!query) {
      controller?.abort();
      results?.replaceChildren();
      return;
    }
    timer = window.setTimeout(() => {
      search(query);
    }, 200);
  });

  input.addEventListener('keydown', (event) => {
    const items = options();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(activeIndex < 0 ? items.length - 1 : activeIndex - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex] instanceof HTMLAnchorElement) {
      event.preventDefault();
      window.location.assign(items[activeIndex].href);
    }
  });
}

dialog?.addEventListener('close', () => {
  if (input instanceof HTMLInputElement) input.setAttribute('aria-expanded', 'false');
});
